import { useMemo } from "react";
import {
  Box, Card, CardContent, LinearProgress, Stack, Typography, alpha,
} from "@mui/material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import { useCycleFilter } from "../../cycleFilterContext";
import { QB_TYPE_DEFS, type QbTypeKey } from "../../utils/qbTypeAliases";

function formatUsd(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

interface BreakdownRow {
  type: string;
  count: number;
  amount: number;
  pct: number;
}

/**
 * TypeBreakdownCard — donut + ranked legend of dollar volume per QB
 * Desktop transaction type posted in this cycle.
 *
 * Donut center shows "% in QBD" — the share of cycle dollars that have
 * landed (vs. the unaccounted slice that's still waiting). Each slice is
 * the QB type colour from `qbTypeAliases` so the donut visually rhymes
 * with the Transactions page tabs.
 */
export function TypeBreakdownCard() {
  const { cycleArg } = useCycleFilter();
  const data = useStableQuery(api.dashboard.getTypeBreakdown, cycleArg, null);

  const rows: BreakdownRow[] = useMemo(() => {
    if (!data) return [];
    return [...data.breakdown]
      .filter((r) => r.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [data]);

  if (!data) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Posted by type
          </Typography>
          <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Posted by type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Nothing has reached QuickBooks Desktop yet in this cycle. Once
            transactions post, you'll see the split across charges, checks,
            deposits, bills, and journal entries here.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const colorFor = (type: string): string => {
    const def = QB_TYPE_DEFS[type as QbTypeKey];
    return def?.color ?? "#b2bec3";
  };
  const labelFor = (type: string): string => {
    const def = QB_TYPE_DEFS[type as QbTypeKey];
    return def?.label ?? type;
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Posted by type
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatUsd(data.total)} in QBD
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <Box sx={{ width: 180, height: 180, position: "relative" }}>
            <ResponsiveContainer>
              <PieChart>
                <Tooltip
                  formatter={(value: any, _name: any, entry: any) => [
                    formatUsd(Number(value) || 0),
                    labelFor(entry?.payload?.type),
                  ]}
                />
                <Pie
                  data={rows}
                  dataKey="amount"
                  nameKey="type"
                  innerRadius={55}
                  outerRadius={82}
                  strokeWidth={2}
                  stroke="transparent"
                  paddingAngle={2}
                >
                  {rows.map((r) => (
                    <Cell key={r.type} fill={colorFor(r.type)} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Types
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {rows.length}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {rows.slice(0, 6).map((r) => {
              const c = colorFor(r.type);
              const def = QB_TYPE_DEFS[r.type as QbTypeKey];
              const Icon = def?.Icon;
              return (
                <Box key={r.type}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.25 }}
                  >
                    {Icon && (
                      <Icon
                        sx={{ fontSize: 16, color: c, flexShrink: 0 }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                    >
                      {labelFor(r.type)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {r.count} • {r.pct.toFixed(0)}%
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 56, textAlign: "right" }}>
                      {formatUsd(r.amount)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: alpha(c, 0.12),
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.min(100, r.pct)}%`,
                        backgroundColor: c,
                        borderRadius: 2,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
